import React, { Component, lazy } from 'react';
import ReactDOM from 'react-dom';
import { observable, action, makeObservable } from 'mobx';
import { observer } from 'mobx-react';

import type { Message, Model } from '@root/models';
import { getAttachmentIconComponent, getAttachmentType } from '@root/models';
import { modalService } from '@components/Modal';

import { PromiseCompletion } from '@classes/PromiseCompletion';
import { helperService } from '@services/helperService';
import modelService from '@services/modelService';
import { wsService } from '@services/wsService';
import { imageService } from '@services/imageService';
import apiService from '@services/apiService';

import { ImgAttach } from '@images/ImgAttach';
import { ImgNewImage } from '@images/ImgNewImage';
import { ImgCollapse } from '@images/ImgCollapse';
import { ImgExpand } from '@images/ImgExpand';
import { ImgMagic } from '@images/ImgMagic';
import { ImgTools } from '@images/ImgTools';
import { ImgFileDoc } from '@images/ImgFileDoc';
import { ImgFileDocx } from '@images/ImgFileDocx';
import { ImgFilePdf } from '@images/ImgFilePdf';
import { ImgFileTxt } from '@images/ImgFileTxt';
import { ImgFileCsv } from '@images/ImgFileCsv';
import { ImgFileHtml } from '@images/ImgFileHtml';
import { ImgFileSvg } from '@images/ImgFileSvg';
import { ImgFileSql } from '@images/ImgFileSql';
import { ImgFileLog } from '@images/ImgFIleLog';
import { ImgFileGeneric } from '@images/ImgFileGeneric';
import { ImageWrapper } from '@components/ImageWrapper';
import ToggleModeButton from '@components/ToggleModeButton';
import TokensCounter from '@components/TokensCounter';
import VoiceRecorder from '@components/MicToText';
import ImageLayoutPicker, { getLayoutIcon, type ImageLayout } from './ImageLayoutPicker';
import ImageModelPicker, { type ImageGenModel } from './ImageModelPicker';
import ImageStylePicker, { parseLoraStyleName } from './ImageStylePicker';
import VideoModelPicker, { type VideoGenModel } from './VideoModelPicker';
import ContextMenu, { ContextMenuItem } from '@components/ContextMenu/index.ts';
import { ImgImage } from '@components/Images/ImgImage';
import { ImgCamera } from '@components/Images/ImgCamera';
import { ImgGallerySelect } from '@components/Images/ImgGallerySelect';
const TextViewerDialog = lazy(() => import("./TextViewerDialog"));
const ImageGalleryPicker = lazy(() => import('./ImageGallery'));

type PromptAreaProps = {
  chatId: string | null;
  isGenerating: boolean;
  isVisible?: boolean;
  currentModel: Model | null;
  isAlternativeMode: boolean;
  hasSystemMessage: boolean;
  tokensInConversation: number;
  predictLength?: string | null;

  showTokenUsage: boolean;
  tools: string[] | null;
  conversation?: Message[];
  onModeSwitch: (isAlternativeMode: boolean) => void;
  onSendMessage: (message: string, attachmentFiles: File[], startFromBeginning: boolean) => void;
  onImageGenerate: (images: File[], promptPositive: string, promptNegative: string, model: ImageGenModel, layout: ImageLayout, styles: string[]) => void;
  onVideoGenerate: (images: File[], prompt: string, model: VideoGenModel, layout: ImageLayout, size: number, fps: number, length: number) => void;
  onCancelRequest: () => void;
  onOpenToolsConfig: () => void;
  onImageGenModeChange?: (active: boolean) => void;
};

@observer
class PromptArea extends Component<PromptAreaProps> {
  @observable inputValue: string;
  @observable private _fileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _docFileRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _allTypesRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _cameraRef: React.RefObject<HTMLInputElement | null> = React.createRef();
  @observable private _attachBtnRef: React.RefObject<HTMLButtonElement | null> = React.createRef();
  @observable private _textareaRef: React.RefObject<HTMLTextAreaElement | null> = React.createRef();
  @observable private _imageBase64List: string[] = [];
  @observable private _imageFiles: File[] = [];
  @observable private _showGalleryPicker: boolean = false;
  @observable private _attachmentFiles: File[] = [];
  @observable private _messageTokens: number | null = null;
  @observable private _isShiftPressed: boolean = false;
  @observable private _isCtrlPressed: boolean = false;
  @observable private _hasKeyboard: boolean = false;
  @observable private _isDragOver: boolean = false;
  @observable private _isVoiceRecording: boolean = false;
  @observable private _keywordSuggestion: string | null = null;
  private _generatePromptCompletion = new PromiseCompletion();
  private _keywordSuggestionTimer: ReturnType<typeof setTimeout> | null = null;
  private _textFragmentPreviews = new Map<File, string>();
  private _maxImagesErrorShownAt: number = 0;
  
  // Image generation mode
  @observable private _isImageGenMode: boolean = localStorage.getItem('isImageGenMode') === 'true';
  @observable private _isVideoGenMode: boolean = localStorage.getItem('isVideoGenMode') === 'true';
  @observable private _imageLayout: ImageLayout = localStorage.getItem('imageLayout') as ImageLayout || 'square';
  @observable private _imageGenModel: ImageGenModel = (() => {
    const stored = localStorage.getItem('imageGenModel');
    if (stored === 'qwen-image-2512' || stored === 'qwen-image-edit-2511') return 'qwen';
    return (stored as ImageGenModel) || 'zImage';
  })();
  @observable private _videoGenModel: VideoGenModel = (localStorage.getItem('videoGenModel') as VideoGenModel) || 'Wan2.2';
  @observable private _videoSize: number = parseInt(localStorage.getItem('videoSize') || '480');
  @observable private _videoFps: number = parseInt(localStorage.getItem('videoFps') || '16');
  @observable private _videoLength: number = parseInt(localStorage.getItem('videoLength') || '5');
  @observable private _selectedStyles: string[] = localStorage.getItem('imageGenStyles') ? JSON.parse(localStorage.getItem('imageGenStyles')!) : [];
  @observable private _imageGenControlsExpanded: boolean = localStorage.getItem('imageGenControlsExpanded') !== 'false';

  private _prefetchModel?: (() => void) & { cancel: () => void };

  private get _isMobile(): boolean {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }

  /** True when the current gen mode accepts an image attachment (video i2v, or image-edit models) */
  private get _imageAttachEnabled(): boolean {
    if (this._isVideoGenMode) return true;
    return this._imageGenModel === 'Flux.Kontext' ||
           this._imageGenModel === 'Flux2.Dev' ||
           this._imageGenModel === 'Flux2.Klein' ||
           this._imageGenModel === 'qwen';
  }

  /** Maximum number of reference images for the current model (0 = t2i, no attachment) */
  private get _maxImages(): number {
    if (this._isVideoGenMode) return 1;
    if (this._imageGenModel === 'qwen' ||
        this._imageGenModel === 'Flux.Kontext' ||
        this._imageGenModel === 'Flux2.Dev' ||
        this._imageGenModel === 'Flux2.Klein') return 3;
    return 0;
  }

  /** Display name for the current image gen model */
  private _getModelDisplayName(model: ImageGenModel): string {
    const map: Record<string, string> = {
      'zImage': 'zImage',
      'SDXL': 'SDXL',
      'Flux1.Dev': 'Flux.1 Dev',
      'Flux.Kontext': 'Flux Kontext',
      'Flux2.Dev': 'Flux.2 Dev',
      'Flux2.Klein': 'Flux.2 Klein',
      'qwen': 'Qwen',
    };
    return map[model] || model;
  }
  
  constructor(props: PromptAreaProps) {
    super(props);
    this.inputValue = localStorage.getItem('promptInput_' + (props.chatId || 'new')) || '';
    makeObservable(this);
  }

  componentDidMount(): void {
    setTimeout(this._recalculateHeight, 0);
    
    this._prefetchModel = helperService.throttle(this._doPrefetchModel, 5000);

    window.addEventListener('keydown', this._onGlobalKeyDown);
    window.addEventListener('keyup', this._onGlobalKeyUp);
  }

  componentWillUnmount(): void {
    this._prefetchModel?.cancel();
    if (this._keywordSuggestionTimer !== null) clearTimeout(this._keywordSuggestionTimer);
    window.removeEventListener('keydown', this._onGlobalKeyDown);
    window.removeEventListener('keyup', this._onGlobalKeyUp);
  }

  componentDidUpdate(prevProps: PromptAreaProps) {
    if (prevProps.chatId !== this.props.chatId) {
      if (this._isImageGenMode) {
        // In image mode preserve the current prompt and save it under the new chat ID
        // so users can iterate on the prompt across generations
        this.save();
      } else {
        this.inputValue = localStorage.getItem('promptInput_' + (this.props.chatId || 'new')) || '';
        setTimeout(this._recalculateHeight, 0);
      }
    }
  }

  @action
  private _onGlobalKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } else if (e.key === 'Control' || e.key === 'Meta') {
      this._isCtrlPressed = true;
      this._hasKeyboard = true;
      e.preventDefault();
    } 
  };

  @action
  private _onGlobalKeyUp = (e: KeyboardEvent) => {
    if (e.key === 'Shift') {
      this._isShiftPressed = false;
    } else if (e.key === 'Control' || e.key === 'Meta') {
      this._isCtrlPressed = false;
    }
  };

  render() {
    const { currentModel, isGenerating, showTokenUsage } = this.props;
    const capabilities = currentModel?.capabilities || [];
    const isImageUploadEnabled = capabilities.includes('vision') || capabilities.includes('tools');
    const hasChat = capabilities.includes('completion');
    const wsConnected = wsService.connectionState === 'connected';
    const wsConnecting = wsService.connectionState === 'connecting';
    const supportTools = capabilities.includes('tools');

    if (!this.props.isVisible) {
      return null; 
    }
    
    const hideButtons = this._isVoiceRecording;
    
    return (
      <>
      <div className="input-container">
        <div className={'input-box' + (isGenerating ? ' loading' : '') + (this._isVoiceRecording ? ' voice-recording' : '')}>
            <div className={'image-gen-panel' + ((this._isImageGenMode && !hideButtons) ? ' active' : '')}>
              <div className="image-gen-panel-inner">
                {this._renderImageGenControls()}
              </div>
            </div>
            <textarea 
              ref={this._textareaRef}
              autoFocus
              value={this.inputValue}
              className={"input-textarea" + (this._isDragOver ? " drag-over" : "") + (hideButtons ? " hidden" : "")}
              placeholder={this._isImageGenMode ? (this._isVideoGenMode ? "Describe the video you want to generate..." : "Describe the image you want to generate...") : "Type your message..."}  
              style={{ height: 66 }} 
              onChange={this._onPromptChange}
              onKeyDown={this._onPromptKeyDown}
              onInput={this._recalculateHeight}
              onPaste={isImageUploadEnabled ? this._onPaste : undefined}
              onFocus={() => setTimeout(this._recalculateHeight, 0)}
              onDragEnter={isImageUploadEnabled ? this._onDragEnter : undefined}
              onDragOver={isImageUploadEnabled ? this._onDragOver : undefined}
              onDragLeave={isImageUploadEnabled ? this._onDragLeave : undefined}
              onDrop={isImageUploadEnabled ? this._onDrop : undefined}
            />
            {this._keywordSuggestion && !this._isVoiceRecording && (
              <div className="keyword-suggestion-hint">
                <span className="keyword-suggestion-icon">✦</span>
                <span className="keyword-suggestion-body">
                  Add keywords: <em>{this._keywordSuggestion}</em>
                </span>
                <button className="keyword-suggestion-accept" onClick={this._acceptKeywordSuggestion} title="Accept (Tab)">
                  ✓ Add
                </button>
                <button className="keyword-suggestion-dismiss" onClick={this._dismissKeywordSuggestion} title="Dismiss (Esc)">
                  ×
                </button>
                <span className="keyword-suggestion-keys"><kbd>Tab</kbd> · <kbd>Esc</kbd></span>
              </div>
            )}
            <div className={"input-controls" + (hideButtons ? " voice-recording" : "")}>
              {isImageUploadEnabled && <input type="file" ref={this._fileRef} accept="image/png,image/jpeg,image/gif,image/webp" multiple style={{ display: 'none' }} onChange={this._onImageUploadChange} />}
              {isImageUploadEnabled && !this._isImageGenMode && <input type="file" ref={this._docFileRef} accept=".pdf,.doc,.docx,.txt,.md,.csv,.js,.ts,.jsx,.tsx,.py,.java,.cpp,.c,.h,.cs,.go,.rb,.php,.html,.css,.xml,.svg,.json,.yaml,.yml,.sql,.log,.sh,.mp3,.webm,.ogg,.wav,.m4a,.flac,.aac" multiple style={{ display: 'none' }} onChange={this._onDocUploadChange} />}
              {isImageUploadEnabled && !this._isImageGenMode && <input type="file" ref={this._allTypesRef} accept="image/png,image/jpeg,image/gif,image/webp,.pdf,.doc,.docx,.txt,.md,.csv,.js,.ts,.jsx,.tsx,.py,.java,.cpp,.c,.h,.cs,.go,.rb,.php,.html,.css,.xml,.svg,.json,.yaml,.yml,.sql,.log,.sh,.mp3,.webm,.ogg,.wav,.m4a,.flac,.aac" multiple style={{ display: 'none' }} onChange={this._onAllTypesChange} />}
              {isImageUploadEnabled && <input type="file" ref={this._cameraRef} accept="image/*" capture="environment" style={{ display: 'none' }} onChange={this._onImageUploadChange} />}
              {isImageUploadEnabled && !hideButtons && <>
                <button
                  ref={this._attachBtnRef}
                  className="attach-btn"
                  onClick={this._isVideoGenMode ? this._onAttachImageClick : undefined}
                  title={this._isVideoGenMode ? 'Attach reference image (i2v)' : 'Attach File'}
                >
                  <ImgAttach size={16} />
                </button>
                {!this._isVideoGenMode && <ContextMenu showOnClick container={this._attachBtnRef}>
                  {() => (<>
                    {this._isMobile && <ContextMenuItem title={<><ImgCamera size={14} /> Camera</>} onClick={this._onCameraClick} />}
                    {this._isMobile && <ContextMenuItem title={<><ImgImage size={14} /> Images</>} onClick={this._onAttachImageClick} />}
                    {!this._isMobile && <ContextMenuItem title={<><ImgAttach size={14} /> From file</>} onClick={this._isImageGenMode ? this._onAttachImageClick : this._onAllTypesClick} />}
                    <ContextMenuItem title={<><ImgGallerySelect size={14} /> Gallery</>} onClick={() => { this._showGalleryPicker = true; }} />
                    {this._isMobile && !this._isImageGenMode && <ContextMenuItem title={<><ImgFileGeneric size={14} /> Files</>} onClick={this._onDocFilesClick} />}
                  </>)}
                </ContextMenu>}
              </>}
              {/* Tools dialog */}
              {!this._isImageGenMode && supportTools && !hideButtons && <button
                className={'attach-btn' + (this._isImageGenMode ? ' disabled' : '')}
                onClick={this.props.onOpenToolsConfig}
                title="Tools Configuration"
              >
                <ImgTools size={14} />
              </button>}
              {!hideButtons && <button 
                className={'attach-btn' + (this._isImageGenMode ? ' active' : '')} 
                onClick={this._onToggleImageGenMode} 
                title="Image Generation Mode"
              >
                <ImgNewImage />
              </button>}

              {this._isImageGenMode && !hideButtons && <span className="image-gen-summary">
                <button onClick={this._toggleImageGenControls} className="attach-btn btn-expand-image-options" title="Show Image Generation Options">
                  <span className="layout-name">{getLayoutIcon(this._imageLayout)}</span>
                  <span className="model-name">{this._isVideoGenMode ? `Video ${this._videoSize}p · ${this._videoFps}fps · ${this._videoLength}s` : this._getModelDisplayName(this._imageGenModel)}</span>
                  <span key={this._imageGenControlsExpanded ? 'collapse' : 'expand'} className="expand-icon-anim">{this._imageGenControlsExpanded ? <ImgCollapse /> : <ImgExpand />}</span>
                </button>
              </span>}
              {this._isImageGenMode && !!this.inputValue && !hideButtons &&<button 
                disabled={this._generatePromptCompletion.isPending || isGenerating}
                className={`attach-btn btn-magic-prompt ${this._generatePromptCompletion.isPending ? 'active' : ''}`} 
                title="Generate Prompt Magic"
                onClick={this._generateMagicPrompt}
              >
                <ImgMagic />
              </button>}
              {this._imageBase64List.length > 0 && !hideButtons && <div className="attached-images">
                {this._imageBase64List.map((base64, index) => (
                  <div key={index} className="attached-image" title={this._imageFiles[index]?.name || 'Image'}>
                    <ImageWrapper
                      src={`data:${imageService.detectImageTypeFast(base64)};base64,${base64}`}
                      className="attached-image-preview"
                      alt={this._imageFiles[index]?.name || 'Attached'}
                    />
                    <button className="remove-btn" onClick={(e) => { e.stopPropagation(); this._onRemoveImage(index); }} title="Remove">&#x2715;</button>
                  </div>
                ))}
              </div>}

              {this._attachmentFiles.length > 0 && !hideButtons && <div className="attached-files">
                {this._attachmentFiles.map((file, index) => {
                  const isTextFragment = file.name.startsWith('text-fragment-') && file.name.endsWith('.txt');

                  if (isTextFragment) {
                    const text = this._textFragmentPreviews.get(file);
                    return (
                      <div key={index} className="attached-text-fragment">
                        <span className="text-fragment-preview">
                          {text || 'Pasted text'}
                        </span>
                        {text && <span className="text-fragment-size">{this._formatSize(file.size)}</span>}
                        <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove">❌</button>
                      </div>
                    );
                  }

                  const attachmentType = getAttachmentType(file.type, file.name);
                  const isViewable = attachmentType === 'text' || attachmentType === 'pdf';
                  const isAudio = attachmentType === 'audio';
                  const iconType = getAttachmentIconComponent(file.type, file.name);
                  
                  // Get appropriate icon component
                  let IconComponent: React.ComponentType<{ size?: number }>;
                  switch (iconType) {
                    case 'pdf': IconComponent = ImgFilePdf; break;
                    case 'doc': IconComponent = ImgFileDoc; break;
                    case 'docx': IconComponent = ImgFileDocx; break;
                    case 'txt': IconComponent = ImgFileTxt; break;
                    case 'csv': IconComponent = ImgFileCsv; break;
                    case 'html': IconComponent = ImgFileHtml; break;
                    case 'svg': IconComponent = ImgFileSvg; break;
                    case 'sql': IconComponent = ImgFileSql; break;
                    case 'log': IconComponent = ImgFileLog; break;
                    default: IconComponent = ImgFileGeneric; break;
                  }
                  
                  if (isAudio) {
                    const audioUrl = URL.createObjectURL(file);
                    return (
                      <div key={index} className="attached-file attached-file-audio">
                        <span className="attached-file-icon">🎵</span>
                        <span className="attached-file-name" title={file.name}>{file.name}</span>
                        <audio src={audioUrl} controls style={{ height: 28, maxWidth: 200 }} />
                        <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove">❌</button>
                      </div>
                    );
                  }
                  
                  return (
                    <div key={index} className="attached-file">
                      <span className="attached-file-icon"><IconComponent size={18} /></span>
                      <span 
                        className={"attached-file-name" + (isViewable ? " viewable" : "")}
                        onClick={isViewable ? () => this._onViewAttachment(file) : undefined}
                        title={isViewable ? "Click to view" : file.name}
                      >
                        {file.name}
                      </span>
                      <button className="remove-btn" onClick={() => this._onRemoveAttachment(index)} title="Remove">❌</button>
                    </div>
                  );
                })}
              </div>}

              <div className={"input-actions" + (hideButtons ? " voice-recording" : "")}>

                {!this._isImageGenMode && this._hasKeyboard && !hideButtons && <div className="key-help">
                  <div className="key-help-bindings">
                    <span className={this._isShiftPressed ? 'help-enabled' : ''}>Shift <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 7v10M19 17H7M7 17l4-4M7 17l4 4"/>
                    </svg> =</span>
                    <span title="Start chat with this message with keeping original system message" className={this._isCtrlPressed ? 'help-enabled' : ''}>Ctrl <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 7v10M19 17H7M7 17l4-4M7 17l4 4"/>
                    </svg> =</span>
                  </div>
                  <div className="key-help-info">
                    <span className={this._isShiftPressed ? 'help-enabled' : ''}>New line</span>
                    <span title="Start chat with this message with keeping original system message" className={this._isCtrlPressed ? 'help-enabled' : ''}>{this.props.isAlternativeMode ? "Send" : "Start over"}</span>
                  </div>
                </div>}

                {showTokenUsage && !hideButtons && !this._isImageGenMode && <TokensCounter
                  isGenerating={isGenerating}
                  className={!this._imageGenControlsExpanded ? "counter-image-gen-combined" : ""}
                  inputValue={this.inputValue}
                  hasImage={this._imageBase64List.length > 0}
                  currentModel={this.props.currentModel}
                  tools={this.props.tools}
                  conversation={this.props.conversation}
                  attachmentFiles={this._attachmentFiles}
                  predictLength={this.props.predictLength}
                  onTokensChange={this._onTokensChange}
                />}

                <VoiceRecorder 
                  onStartRecording={this._onStartRecording} 
                  onTranscription={this._onTranscriptionDone}
                  onRecordingChange={this._onRecordingChange}
                />

                {isGenerating && !hideButtons && <button className="btn btn-secondary" onClick={this._onCancelRequestClick}>Cancel</button>}
                {!hideButtons && <ToggleModeButton
                  className="btn btn-primary"
                  title={wsConnecting ? 'Connecting...' : this._isImageGenMode ? "Generate" : "Send"}
                  isAlternativeMode={this.props.isAlternativeMode}
                  onModeSwitch={this.props.onModeSwitch}
                  alternativeTitle="Start"
                  style={{ display: 'block', minWidth: 55 }}
                  onClick={() => this._sendMessage()}
                  onClickAlternative={() => this._sendMessage(true)}
                  disabled={!hasChat || !wsConnected || this._generatePromptCompletion.isPending || currentModel?.isDisabled}
                  visible={!isGenerating}
                />}
              </div>
            </div>
        </div>
      </div>

      {this._showGalleryPicker && ReactDOM.createPortal(
        <React.Suspense fallback={null}>
          <ImageGalleryPicker
            pickerMode={{ maxSelect: 3, onConfirm: this._onGalleryPickerConfirm }}
            onClose={() => { this._showGalleryPicker = false; }}
            onNavigateToChat={() => {}}
          />
        </React.Suspense>,
        document.body
      )}
      </>
    );
  }

  /** Add a reference image from an existing attachment ID (e.g. "Use as reference" from chat message) */
  @action.bound
  public async addImageFromAttachmentId(attachmentId: string): Promise<void> {
    // Auto-switch to last used i2i model if needed
    if (!this._imageAttachEnabled && !this._isVideoGenMode) {
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      this._setImageGenModel(last || 'Flux2.Klein');
    }
    if (this._maxImages > 0 && this._imageFiles.length >= this._maxImages) {
      this._showMaxImagesError();
      return;
    }
    try {
      const response = await fetch(`/image/${attachmentId}`);
      const blob = await response.blob();
      const file = new File([blob], `${attachmentId}.jpg`, { type: blob.type || 'image/jpeg' });
      const base64 = await imageService.readFileAsBase64(file);
      this._imageBase64List.push(base64);
      this._imageFiles.push(file);
    } catch (e) {
      console.error('Failed to load attachment image:', e);
    }
  }

  /** Called when gallery picker confirms selection */
  @action.bound
  private async _onGalleryPickerConfirm(ids: string[]): Promise<void> {
    this._showGalleryPicker = false;
    for (const id of ids) {
      await this.addImageFromAttachmentId(id);
    }
  }

  private _renderImageGenControls() {
    // for video gen mode we show a simplified UI and hide the style picker since styles are not supported by video gen models
    if (this._isVideoGenMode && !this._imageGenControlsExpanded) return null;
    // Note: collapse animation is handled by CSS on .image-gen-expandable
    return (
      <div className="image-gen-controls image-gen-controls-models">
        <div className={"image-gen-expandable" + (this._imageGenControlsExpanded ? " expanded" : "")}>
          <div className="image-gen-expandable-inner">

          {/* Line 1: layout proportions + image/video mode toggle */}
          <div className="control-line">
            <ImageLayoutPicker layout={this._imageLayout} onSelect={this._setImageLayout} />
            <div className="gen-mode-toggle">
              <button
                className={'gen-mode-btn' + (!this._isVideoGenMode ? ' active' : '')}
                onClick={this._setImageMode}
                title="Image Generation"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/>
                  <polyline points="21 15 16 10 5 21"/>
                </svg>
                <span>Image</span>
              </button>
              <button
                className={'gen-mode-btn' + (this._isVideoGenMode ? ' active' : '')}
                onClick={this._setVideoMode}
                title="Video Generation"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/>
                </svg>
                <span>Video</span>
              </button>
            </div>
          </div>

          {/* Line 2: model picker */}
          <div className="control-line">
            {!this._isVideoGenMode
              ? <ImageModelPicker model={this._imageGenModel} onSelect={this._setImageGenModel} hasImages={this._imageFiles.length > 0} />
              : <VideoModelPicker model={this._videoGenModel} onSelect={this._setVideoGenModel} />
            }
          </div>

          {/* Line 3: video parameters (video mode only) */}
          {this._isVideoGenMode && (
            <div className="control-line video-params-line">
              <div className="video-param-group">
                <label>Size</label>
                <div className="video-param-options">
                  {([480, 640, 720] as const).map(s => (
                    <button key={s} className={'video-param-btn' + (this._videoSize === s ? ' active' : '')} onClick={() => this._setVideoSize(s)}>{s}p</button>
                  ))}
                </div>
              </div>
              <div className="video-param-group">
                <label>FPS</label>
                <div className="video-param-options">
                  {([8, 12, 16, 24] as const).map(f => (
                    <button key={f} className={'video-param-btn' + (this._videoFps === f ? ' active' : '')} onClick={() => this._setVideoFps(f)}>{f}</button>
                  ))}
                </div>
              </div>
              <div className="video-param-group">
                <label>Length</label>
                <div className="video-param-options">
                  {([5, 10, 15] as const).map(l => (
                    <button key={l} className={'video-param-btn' + (this._videoLength === l ? ' active' : '')} onClick={() => this._setVideoLength(l)}>{l}s</button>
                  ))}
                </div>
              </div>
            </div>
          )}

          </div>
        </div>

          {/* Line 4: style picker — image mode only */}
        {!this._isVideoGenMode && <ImageStylePicker
          model={this._imageGenModel}
          selectedStyles={this._selectedStyles}
          onChange={this._setSelectedStyles}
        />}
      </div>
    );
  }

  private _recalculateHeight = () => {
      this._textareaRef.current!.style.height = 'auto';
      const newHeight = Math.min(192, Math.max(48, this._textareaRef.current!.scrollHeight));
      this._textareaRef.current!.style.height = newHeight + 'px';
  };

  @action
  _onPromptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Tab' && this._keywordSuggestion !== null) {
      e.preventDefault();
      this._acceptKeywordSuggestion();
      return;
    }
    if (e.key === 'Escape' && this._keywordSuggestion !== null) {
      this._dismissKeywordSuggestion();
      return;
    }
    if (e.key === 'Escape' && this._imageGenControlsExpanded && this._isImageGenMode) {
      this._toggleImageGenControls();
      return;
    }
    
    if (e.key === 'Enter') {
      if (!e.shiftKey) {
        const isChangeMode = e.altKey || e.ctrlKey || e.metaKey;
        e.preventDefault();
        const startFromBeginning = !this.props.isAlternativeMode && isChangeMode || this.props.isAlternativeMode && !isChangeMode;
        this._sendMessage(startFromBeginning);
        return;
      }

      if (!e.shiftKey) {
        const textarea = this._textareaRef.current!;
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        
        // Insert newline at cursor
        const before = this.inputValue.substring(0, start);
        const after = this.inputValue.substring(end);
        this.inputValue = before + "\n" + after;

        this._textareaRef.current!.value = this.inputValue;
        textarea.selectionStart = textarea.selectionEnd = start + 1;
      }
      setTimeout(this._recalculateHeight, 0);
    }
  };

  @action
  _onPaste = async (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (this._isImageGenMode && !this._imageAttachEnabled) {
      // Standard image gen mode: no attachments, let browser handle plain text naturally
      return;
    }

    const clipboardData = e.clipboardData;
    if (!clipboardData) return;

    const text = clipboardData.getData('text/plain');
    // Prevent default immediately to avoid text being pasted before we process it
    e.preventDefault();

    const result = await imageService.getAttachmentsFromDataTransfer(clipboardData, this._isShiftPressed);

    if (result.files.length) {
      for (const file of result.files) {
        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        } else if (!(this._isImageGenMode && this._imageAttachEnabled)) {
          // Non-image files only blocked in image-attach (video/image-edit) modes
          this._attachmentFiles.push(file);
        }
      }
      return;
    } 
    
    if (result.textFragment) {
      if (!(this._isImageGenMode && this._imageAttachEnabled)) {
        // Text fragment attachment blocked only in image-attach modes
        const textFile = imageService.createTextFragmentFile(result.textFragment);
        this._textFragmentPreviews.set(textFile, result.textFragment.trim().substring(0, 80) + (result.textFragment.length > 80 ? '...' : ''));
        this._attachmentFiles.push(textFile);
        return;
      }
      // In image-attach mode fall through and paste as plain text
    }
    
    // Regular text paste - use execCommand for proper undo history and cursor placement
    if (text) {
      const textarea = this._textareaRef.current!;
      const inserted = document.execCommand('insertText', false, text);
      if (inserted) {
        this.inputValue = textarea.value;
        this._recalculateHeight();
      } else {
        // Fallback for environments where execCommand is unavailable
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const before = this.inputValue.substring(0, start);
        const after = this.inputValue.substring(end);
        this.inputValue = before + text + after;
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + text.length;
          this._recalculateHeight();
        }, 0);
      }
      this.save();
    }
  };

  @action
  private _onDragEnter = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = true;
  };

  @action
  private _onDragOver = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  @action
  private _onDragLeave = (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;
  };

  @action
  private _onDrop = async (e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    e.stopPropagation();
    this._isDragOver = false;

    const files = e.dataTransfer.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
        continue;
      }

      if (imageService.isImageFile(file)) {
        await this._processImageFile(file);
      } else if (!(this._isImageGenMode && this._imageAttachEnabled)) {
        // Non-image files only blocked in image-attach (video/image-edit) modes
        this._attachmentFiles.push(file);
      }
    }
  };

  @action
  _onImageUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._fileRef.current!.value = '';
      return;
    }

    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
          continue;
        }

        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        }
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._fileRef.current!.value = '';
  }

  @action
  private _processImageFile = async (file: File): Promise<void> => {
    // Auto-switch to last used i2i model when attaching an image in non-i2i mode
    if (!this._imageAttachEnabled && !this._isVideoGenMode) {
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      this._setImageGenModel(last || 'Flux2.Klein');
    }
    if (this._imageFiles.length >= this._maxImages && this._maxImages > 0) {
      this._showMaxImagesError();
      return;
    }
    const base64 = await imageService.readFileAsBase64(file);
    this._imageBase64List.push(base64);
    this._imageFiles.push(file);
  };

  private _sendMessageInternal = async (content: string, startFromBeginning: boolean = false) => {
    // Dismiss any pending keyword suggestion
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }

    // If in image generation mode, append generation parameters to message
    let messageToSend = content;
    if (this._isImageGenMode) {
      // Video generation mode
      if (this._isVideoGenMode) {
        this.inputValue = '';
        const imageFiles = [...this._imageFiles];
        this._imageBase64List = [];
        this._imageFiles = [];
        this._attachmentFiles = [];
        this.props.onVideoGenerate(imageFiles, messageToSend, this._videoGenModel, this._imageLayout, this._videoSize, this._videoFps, this._videoLength);
        setTimeout(this._recalculateHeight, 0);
        return;
      }

      // Image generation mode
      const styles = this._selectedStyles;

      for (const style of styles) {
        const styleParsed = parseLoraStyleName(style);
        // search if any of the keywords appear in the prompt
        if (styleParsed.keywords) {
          let keywordMatched = false;
          for (const keyword of styleParsed.keywords) {
            if (messageToSend.toLowerCase().includes(keyword.toLowerCase())) {
              keywordMatched = true;
              break;
            }
          }
          if (!keywordMatched) {
            // add first keyword to the prompt
            messageToSend += `. ${styleParsed.keywords[0]}`;
          }
        }
      }
      
      this.props.onImageGenerate(
        [...this._imageFiles],
        messageToSend,
        '',
        this._imageGenModel,
        this._imageLayout,
        styles
      );
      return;
    }
    
    this.inputValue = '';
    const allFiles: File[] = [];
    allFiles.push(...this._imageFiles);
    allFiles.push(...this._attachmentFiles);
    this.props.onSendMessage(messageToSend, allFiles, startFromBeginning);
    if (this._fileRef.current) {
      this._fileRef.current.value = '';
    }
    this._imageBase64List = [];
    this._imageFiles = [];
    this._attachmentFiles = [];
    setTimeout(this._recalculateHeight, 0);
  }

  @action
  private _sendMessage = async (startFromBeginning: boolean = false) => {
    const value = this.inputValue.trim();

    const canSend = !!value || (this._imageBase64List.length > 0 || this._attachmentFiles.length > 0) && this.props.hasSystemMessage;

    if (!canSend){
      this._textareaRef.current?.focus();
      return;
    }

    this._sendMessageInternal(value, startFromBeginning);
  }

  @action
  private _onPromptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    this.inputValue = e.target.value;
    this.save();
    if (!this._isImageGenMode){
      this._prefetchModel?.();
    }
    // Reset suggestion on every keystroke, then reschedule after 2 s of silence
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
    if (this._isImageGenMode && !this._isVideoGenMode && this._selectedStyles.length > 0 && this.inputValue.trim()) {
      this._keywordSuggestionTimer = setTimeout(action(() => {
        this._keywordSuggestion = this._computeKeywordSuggestion();
        this._keywordSuggestionTimer = null;
      }), 2000);
    }
  };

  public save() {
    localStorage.setItem('promptInput_' + (this.props.chatId || 'new'), this.inputValue);
  }

  private _doPrefetchModel = async () => {
    const currentModel = this.props.currentModel;
    let expectedMinContext = this.props.tokensInConversation + (this._messageTokens || 0);
    if (currentModel?.name === 'gpt-oss:20b') {
      // gpt-oss:20b requires special handling due to its large context size
      expectedMinContext = Math.max(expectedMinContext, 61440);
    }

    const activeModel = modelService.activeModels.find(activeModel =>
      activeModel.server === currentModel?.server && activeModel.name === currentModel?.name
    );

    if (activeModel || !currentModel) return;

    await modelService.prefetchModel(currentModel, expectedMinContext);
  };

  @action.bound
  private _onTokensChange(tokens: number | null) {
    this._messageTokens = tokens;
  }

  @action
  private _onCancelRequestClick = () => {
    this.props.onCancelRequest();
  }

  @action
  private _onAttachImageClick = () => {
    if (this._fileRef.current) {
      this._fileRef.current.click();
    }
  }

  @action
  private _onCameraClick = () => {
    if (this._cameraRef.current) {
      this._cameraRef.current.click();
    }
  }

  @action
  private _onAllTypesClick = () => {
    if (this._allTypesRef.current) {
      this._allTypesRef.current.click();
    }
  }

  @action
  private _onDocFilesClick = () => {
    if (this._docFileRef.current) {
      this._docFileRef.current.click();
    }
  }

  @action
  _onAllTypesChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._allTypesRef.current!.value = '';
      return;
    }
    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) continue;
        if (imageService.isImageFile(file)) {
          await this._processImageFile(file);
        } else {
          this._attachmentFiles.push(file);
        }
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._allTypesRef.current!.value = '';
  }

  @action
  _onDocUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) {
      this._docFileRef.current!.value = '';
      return;
    }

    try {
      for (const file of Array.from(files)) {
        if (!imageService.isFileSupported(file) || !imageService.validateAttachmentFile(file)) {
          continue;
        }
        this._attachmentFiles.push(file);
      }
    } catch (error) {
      console.error('Error processing uploaded file:', error);
      alert('Failed to process the uploaded file.');
    }
    this._docFileRef.current!.value = '';
  }

  @action
  private _onRemoveImage = async (index: number) => {
    if (!await modalService.showConfirmation('Remove this image?', 'Remove Image')) return;
    this._imageBase64List.splice(index, 1);
    this._imageFiles.splice(index, 1);
    if (this._imageBase64List.length === 0 && this._fileRef.current) {
      this._fileRef.current.value = '';
    }
  }

  @action
  private _onRemoveAttachment = (index: number) => {
    this._textFragmentPreviews.delete(this._attachmentFiles[index]);
    this._attachmentFiles.splice(index, 1);
  }

  @action.bound
  private async _onViewAttachment(file: File) {
    try {
      const attachmentType = getAttachmentType(file.type, file.name);
      
      if (attachmentType === 'pdf') {
        // For PDF files being edited, we need to create a temporary attachment ID
        // For now, we'll show an alert that PDF preview is only available after sending
        alert('PDF preview will be available after sending the message.');
        return;
      }
      
      // Read the file content
      const text = await file.text();
      
      await modalService.show(TextViewerDialog, {
        attachmentId: '',
        filename: file.name,
        content: text,
        model: this.props.currentModel?.name
      });
    } catch (error) {
      console.error('Failed to read file:', error);
      alert('Failed to read file content.');
    }
  }

  @action
  private _onToggleImageGenMode = () => {
    this._isImageGenMode = !this._isImageGenMode;
    localStorage.setItem('isImageGenMode', this._isImageGenMode ? 'true' : 'false');
    if (this._isImageGenMode) {
      // Reset to defaults when entering image gen mode
      this._imageLayout = localStorage.getItem('imageLayout') as ImageLayout || 'square';
      this._imageGenModel = localStorage.getItem('imageGenModel') as ImageGenModel || 'zImage';
      this._selectedStyles = localStorage.getItem('imageGenStyles') ? JSON.parse(localStorage.getItem('imageGenStyles')!) : [];
    }
    this.props.onImageGenModeChange?.(this._isImageGenMode);
  }

  @action.bound
  private _toggleImageGenControls = () => {
    this._imageGenControlsExpanded = !this._imageGenControlsExpanded;
    localStorage.setItem('imageGenControlsExpanded', this._imageGenControlsExpanded ? 'true' : 'false');
  }

  @action.bound
  private _setImageLayout = (layout: ImageLayout) => {
    this._imageLayout = layout;
    localStorage.setItem('imageLayout', layout);
  }

  @action.bound
  private _setImageGenModel = (model: ImageGenModel) => {
    const i2iModels: ImageGenModel[] = ['Flux.Kontext', 'Flux2.Dev', 'Flux2.Klein', 'qwen'];
    if (i2iModels.includes(model)) {
      localStorage.setItem('lastImageEditModel', model);
    } else if (this._imageFiles.length > 0) {
      // Non-i2i model selected while images are attached → keep on last used i2i
      const last = localStorage.getItem('lastImageEditModel') as ImageGenModel | null;
      model = last || 'Flux2.Klein';
    }
    this._imageGenModel = model;
    localStorage.setItem('imageGenModel', model);
  }

  @action.bound
  private _setSelectedStyles = (styles: string[]) => {
    this._selectedStyles = styles;
    localStorage.setItem('imageGenStyles', JSON.stringify(styles));
    // Clear stale suggestion since the selected set changed
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
  }

  @action.bound
  private _setVideoGenModel = (model: VideoGenModel) => {
    this._videoGenModel = model;
    localStorage.setItem('videoGenModel', model);
  }

  @action.bound
  private _setVideoSize = (size: number) => {
    this._videoSize = size;
    localStorage.setItem('videoSize', String(size));
  }

  @action.bound
  private _setVideoFps = (fps: number) => {
    this._videoFps = fps;
    localStorage.setItem('videoFps', String(fps));
  }

  @action.bound
  private _setVideoLength = (length: number) => {
    this._videoLength = length;
    localStorage.setItem('videoLength', String(length));
  }

  @action.bound
  private _setImageMode = () => {
    this._isVideoGenMode = false;
    localStorage.setItem('isVideoGenMode', 'false');
  }

  @action.bound
  private _setVideoMode = () => {
    this._isVideoGenMode = true;
    localStorage.setItem('isVideoGenMode', 'true');
  }

  public focus() {
    this._textareaRef.current?.focus();
  }

  @action.bound
  private _onTranscriptionDone(text: string) {
    if (!text) return;
    this.inputValue += text;
    this.save();
    setTimeout(this._recalculateHeight, 0);
    // start request automatically after transcription
    this._sendMessage();
  }

  @action.bound
  private _onStartRecording = () => {
    this._prefetchModel?.();
  }

  @action.bound
  private _onRecordingChange = (isRecording: boolean) => {
    this._isVoiceRecording = isRecording;
  }

  @action.bound
  private async _generateMagicPrompt() {
    // call server /api/magic-prompt with selected text, pass current model, layout and styles
    const body = {
      llmModel: this.props.currentModel?.name || 'gpt-oss:20b',
      imageModel: this._imageGenModel,
      layout: this._imageLayout,
      styles: this._selectedStyles,
      prompt: this.inputValue.trim()
    }

    const data = await apiService.post<{ prompt: string }>('/api/magic-prompt', body, {
      loader: this._generatePromptCompletion
    });

    // update textarea with new prompt
    await this._sendMessageInternal(data.prompt);
  }

  @action
  public setImageSettings(settings: { prompt?: string; orientation?: ImageLayout; type?: ImageGenModel; styles?: string[] }) {
    if (settings.orientation) {
      this._setImageLayout(settings.orientation);
    }
    if (settings.type) {
      this._setImageGenModel(settings.type);
    }
    if (settings.styles) {
      this._setSelectedStyles(settings.styles);
    }
    if (settings.prompt) {
      this.inputValue = settings.prompt;
    }

    this._isImageGenMode = true;
    localStorage.setItem('isImageGenMode', 'true');
  }

  /** Returns missing lora keywords (comma-separated) that should be added to the prompt, or null if nothing is missing */
  private _computeKeywordSuggestion(): string | null {
    if (!this._isImageGenMode || this._isVideoGenMode || this._selectedStyles.length === 0) return null;
    const prompt = this.inputValue.toLowerCase().trim();
    if (!prompt) return null;
    const missing: string[] = [];
    for (const styleId of this._selectedStyles) {
      const parsed = parseLoraStyleName(styleId);
      if (!parsed.keywords || parsed.keywords.length === 0) continue;
      const hasKeyword = parsed.keywords.some(kw => prompt.includes(kw.toLowerCase()));
      if (!hasKeyword) {
        missing.push(parsed.keywords[0]);
      }
    }
    return missing.length > 0 ? missing.join(', ') : null;
  }

  @action
  private _acceptKeywordSuggestion = () => {
    if (!this._keywordSuggestion) return;
    this.inputValue = this.inputValue.trimEnd() + ', ' + this._keywordSuggestion;
    this._keywordSuggestion = null;
    this.save();
    setTimeout(this._recalculateHeight, 0);
    this._textareaRef.current?.focus();
  }

  private _showMaxImagesError() {
    const now = Date.now();
    if (now - this._maxImagesErrorShownAt < 1000) return;
    this._maxImagesErrorShownAt = now;
    modalService.showError(
      `This model supports up to ${this._maxImages} reference image${this._maxImages === 1 ? '' : 's'}. Remove an existing image to add a new one.`,
      'Maximum images reached'
    );
  }

  @action
  private _dismissKeywordSuggestion = () => {
    this._keywordSuggestion = null;
    if (this._keywordSuggestionTimer !== null) {
      clearTimeout(this._keywordSuggestionTimer);
      this._keywordSuggestionTimer = null;
    }
    this._textareaRef.current?.focus();
  }

  private _formatSize(size: number) {
    const sizeStr = size < 1024
      ? `${size} B`
      : size < 1024 * 1024
        ? `${(size / 1024).toFixed(1)} KB`
        : `${(size / (1024 * 1024)).toFixed(1)} MB`;

    return sizeStr;
  }

}

export default PromptArea;